import React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { CheckCircleIcon, InfoIcon, ReportIcon, WarningIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

type ToastAction = Readonly<{
	label: string | React.ReactNode;
	onClick: () => void;
}>;

type Props = Readonly<{
	variant: ToastVariant;
	message: string | React.ReactNode;
	action?: ToastAction;
	onActionClick: () => void;
}>;

const variantStyles = {
	success: {
		bg: 'bg-fill-success-weaker',
		border: 'border-stroke-success',
		text: 'text-typography-success',
		iconClass: 'text-icon-success',
		Icon: CheckCircleIcon,
	},
	error: {
		bg: 'bg-fill-error-subtle',
		border: 'border-stroke-error',
		text: 'text-typography-error',
		iconClass: 'text-icon-error',
		Icon: ReportIcon,
	},
	warning: {
		bg: 'bg-fill-warning-subtle',
		border: 'border-stroke-warning',
		text: 'text-typography-warning',
		iconClass: 'text-icon-warning',
		Icon: WarningIcon,
	},
	info: {
		bg: 'bg-fill-info-subtle',
		border: 'border-stroke-info',
		text: 'text-typography-primary',
		iconClass: 'text-icon-info',
		Icon: InfoIcon,
	},
};

export const Toast: React.FC<Props> = ({ variant, message, action, onActionClick }) => {
	const currentVariant = variantStyles[variant];
	const IconComponent = currentVariant.Icon;

	return (
		<div
			className={cn(
				'flex flex-col gap-4 rounded-lg border px-4 py-3 md:flex-row md:items-center md:gap-4',
				currentVariant.bg,
				currentVariant.border,
			)}
		>
			<div className="flex items-start gap-4 md:flex-1 md:items-center">
				<div className="shrink-0">
					<IconComponent className={currentVariant.iconClass} />
				</div>
				<Text className={cn(currentVariant.text, 'font-bold')}>{message}</Text>
			</div>
			{action && (
				<Button
					className="h-9 w-full shrink-0 md:w-auto"
					onClick={onActionClick}
					variant="secondary-solid"
					size="small"
				>
					{action.label}
				</Button>
			)}
		</div>
	);
};
